import { ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { InputField } from "../../components/ui";
import type { GtmBrain, GtmBrainDoc } from "../api/GtmApi";
import GtmBrainDocSection from "../components/GtmBrainDocSection";

export type BrainEditorSectionId =
  | "market"
  | "icp"
  | "company"
  | "voice"
  | "signals"
  | "personas"
  | "pillars"
  | "objections"
  | "competitors"
  | "influencers"
  | "keywords";

function listText(values?: string[] | null) {
  return (values || []).join(", ");
}

function parseList(text: string) {
  return text
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function Section({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: BrainEditorSectionId;
  title: string;
  open: boolean;
  onToggle: (id: BrainEditorSectionId) => void;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-gray-50"
        onClick={() => onToggle(id)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
        <span className="text-sm font-semibold text-gray-900">{title}</span>
      </button>
      {open ? <div className="space-y-3 border-t border-gray-100 px-4 py-4">{children}</div> : null}
    </section>
  );
}

type Props = {
  brain: GtmBrain;
  docs: GtmBrainDoc[];
  openSections: Record<BrainEditorSectionId, boolean>;
  onToggleSection: (id: BrainEditorSectionId) => void;
  onBrainChange: (next: GtmBrain) => void;
  onDocsChange: (next: GtmBrainDoc[]) => void;
};

export default function GtmBrainEditorPanel({
  brain,
  docs,
  openSections,
  onToggleSection,
  onBrainChange,
  onDocsChange,
}: Props) {
  const { t } = useTranslation();

  const setField = (key: keyof GtmBrain, value: string | string[]) => {
    onBrainChange({ ...brain, [key]: value });
  };

  return (
    <div className="space-y-3 pb-8">
      <Section id="market" title={t("Market profile")} open={openSections.market} onToggle={onToggleSection}>
        <InputField
          mode="textarea"
          rows={3}
          label={t("Value proposition")}
          value={brain.valueProposition || ""}
          onChange={(v) => setField("valueProposition", v)}
        />
        <InputField
          mode="textarea"
          rows={3}
          label={t("Business summary")}
          value={brain.businessSummary || ""}
          onChange={(v) => setField("businessSummary", v)}
        />
        <InputField
          mode="textarea"
          rows={2}
          label={t("Offering")}
          value={brain.offering || ""}
          onChange={(v) => setField("offering", v)}
        />
        <InputField
          mode="textarea"
          rows={2}
          label={t("Key features")}
          value={brain.keyFeatures || ""}
          onChange={(v) => setField("keyFeatures", v)}
        />
        <InputField
          mode="textarea"
          rows={2}
          label={t("Social proof")}
          value={brain.socialProof || ""}
          onChange={(v) => setField("socialProof", v)}
        />
        <InputField
          label={t("Industries (comma-separated)")}
          value={listText(brain.industries)}
          onChange={(v) => setField("industries", parseList(v))}
        />
        <InputField
          label={t("Geographies (comma-separated)")}
          value={listText(brain.geographies)}
          onChange={(v) => setField("geographies", parseList(v))}
        />
      </Section>

      <Section id="icp" title={t("ICP")} open={openSections.icp} onToggle={onToggleSection}>
        <InputField
          mode="textarea"
          rows={3}
          label={t("ICP summary")}
          value={brain.icpSummary || ""}
          onChange={(v) => setField("icpSummary", v)}
        />
        <InputField
          label={t("Target company size")}
          value={brain.targetCompanySize || ""}
          onChange={(v) => setField("targetCompanySize", v)}
        />
        <InputField
          label={t("Verticals (comma-separated)")}
          value={listText(brain.verticals)}
          onChange={(v) => setField("verticals", parseList(v))}
        />
        <InputField
          label={t("Buying roles (comma-separated)")}
          value={listText(brain.buyingRoles)}
          onChange={(v) => setField("buyingRoles", parseList(v))}
        />
        <InputField
          label={t("Champion roles (comma-separated)")}
          value={listText(brain.championRoles)}
          onChange={(v) => setField("championRoles", parseList(v))}
        />
        <InputField
          label={t("User roles (comma-separated)")}
          value={listText(brain.userRoles)}
          onChange={(v) => setField("userRoles", parseList(v))}
        />
      </Section>

      <Section id="company" title={t("Company")} open={openSections.company} onToggle={onToggleSection}>
        <InputField
          label={t("Company name")}
          value={brain.companyName || ""}
          onChange={(v) => setField("companyName", v)}
        />
        <InputField
          label={t("Domain")}
          value={brain.domain || ""}
          onChange={(v) => setField("domain", v)}
        />
        <InputField
          label={t("LinkedIn URL")}
          value={brain.linkedinUrl || ""}
          onChange={(v) => setField("linkedinUrl", v)}
        />
        <InputField
          label={t("Company size")}
          value={brain.companySize || ""}
          onChange={(v) => setField("companySize", v)}
        />
        <InputField
          label={t("Business model")}
          value={brain.businessModel || ""}
          onChange={(v) => setField("businessModel", v)}
        />
        <InputField
          label={t("Stage")}
          value={brain.stage || ""}
          onChange={(v) => setField("stage", v)}
        />
        <InputField
          label={t("Language")}
          value={brain.language || ""}
          onChange={(v) => setField("language", v)}
        />
      </Section>

      <Section id="voice" title={t("Voice")} open={openSections.voice} onToggle={onToggleSection}>
        <InputField
          mode="textarea"
          rows={2}
          label={t("Tone of voice")}
          value={brain.toneOfVoice || ""}
          onChange={(v) => setField("toneOfVoice", v)}
        />
        <InputField
          mode="textarea"
          rows={2}
          label={t("LinkedIn voice")}
          value={brain.voiceLinkedin || ""}
          onChange={(v) => setField("voiceLinkedin", v)}
        />
        <InputField
          mode="textarea"
          rows={2}
          label={t("Blog voice")}
          value={brain.voiceBlog || ""}
          onChange={(v) => setField("voiceBlog", v)}
        />
      </Section>

      <Section
        id="signals"
        title={t("Signals & meeting")}
        open={openSections.signals}
        onToggle={onToggleSection}
      >
        <InputField
          mode="textarea"
          rows={4}
          label={t("Signals (JSON)")}
          value={brain.signalsJson || ""}
          onChange={(v) => setField("signalsJson", v)}
        />
        <InputField
          label={t("Calendar URL")}
          value={brain.calendarUrl || ""}
          onChange={(v) => setField("calendarUrl", v)}
        />
      </Section>

      <Section id="personas" title={t("Personas")} open={openSections.personas} onToggle={onToggleSection}>
        <GtmBrainDocSection title={t("Persona list")} docType="persona" docs={docs} onChange={onDocsChange} />
      </Section>

      <Section
        id="pillars"
        title={t("Message pillars")}
        open={openSections.pillars}
        onToggle={onToggleSection}
      >
        <GtmBrainDocSection title={t("Pillar list")} docType="pillar" docs={docs} onChange={onDocsChange} />
      </Section>

      <Section
        id="objections"
        title={t("Objections")}
        open={openSections.objections}
        onToggle={onToggleSection}
      >
        <GtmBrainDocSection
          title={t("Objection list")}
          docType="objection"
          docs={docs}
          onChange={onDocsChange}
        />
      </Section>

      <Section
        id="competitors"
        title={t("Competitors")}
        open={openSections.competitors}
        onToggle={onToggleSection}
      >
        <GtmBrainDocSection
          title={t("Competitor list")}
          docType="competitor"
          docs={docs}
          onChange={onDocsChange}
        />
      </Section>

      <Section
        id="influencers"
        title={t("Influencers")}
        open={openSections.influencers}
        onToggle={onToggleSection}
      >
        <GtmBrainDocSection
          title={t("Influencer list")}
          docType="influencer"
          docs={docs}
          onChange={onDocsChange}
        />
      </Section>

      <Section id="keywords" title={t("Keywords")} open={openSections.keywords} onToggle={onToggleSection}>
        <GtmBrainDocSection title={t("Keyword list")} docType="keyword" docs={docs} onChange={onDocsChange} />
      </Section>
    </div>
  );
}
